import { useMemo, useState } from 'react';
import DemoBadge from '@/components/feature/DemoBadge';
import { REGIONS, formatUSD } from '@/pages/demo/revenue/components/revenueData';
import { CHARITY_BY_REGION, CHARITY_TOTAL_RAISED, CHARITY_PROGRAMS, CHARITY_CONCEPTS, regionName } from './adminData';

function Kpi({ label, value, note, icon }: { label: string; value: string; note?: string; icon: string }) {
  return (
    <div className="bg-background-50 border border-background-200 rounded-lg p-4 md:p-5">
      <div className="flex items-center justify-between gap-2">
        <p className="font-label uppercase tracking-widest text-[10px] md:text-[11px] text-foreground-500">{label}</p>
        <span className="w-8 h-8 rounded-md bg-accent-100 text-accent-800 flex items-center justify-center shrink-0"><i className={icon}></i></span>
      </div>
      <p className="mt-2.5 font-heading font-extrabold text-2xl md:text-3xl text-foreground-950 leading-none">{value}</p>
      {note && <p className="mt-1.5 text-[11px] text-foreground-500">{note}</p>}
    </div>
  );
}

export default function CharityFundraising() {
  const [scope, setScope] = useState<string>('all');

  const programs = useMemo(() => {
    const allowed = new Set(
      scope === 'all'
        ? REGIONS.map((r) => r.id)
        : scope === 'california'
          ? REGIONS.filter((r) => r.state === 'California').map((r) => r.id)
          : scope === 'arizona'
            ? REGIONS.filter((r) => r.state === 'Arizona').map((r) => r.id)
            : [scope],
    );
    return CHARITY_PROGRAMS.filter((p) => allowed.has(p.regionId));
  }, [scope]);

  const raisedForScope = programs.reduce((a, p) => a + p.raised, 0);
  const goalForScope = programs.reduce((a, p) => a + p.goal, 0);

  const scopeOptions = [
    { id: 'all', label: 'All regions' },
    { id: 'california', label: 'California' },
    { id: 'arizona', label: 'Arizona' },
  ];

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center gap-3">
        <h2 className="font-heading font-extrabold uppercase text-xl md:text-2xl text-foreground-950">Charity fundraising</h2>
        <DemoBadge label="Concept — sample data" />
      </div>

      <div className="flex items-start gap-3 bg-secondary-50 border border-secondary-200 rounded-md px-4 py-3">
        <span className="w-5 h-5 flex items-center justify-center text-secondary-700 shrink-0 mt-0.5"><i className="ri-information-line"></i></span>
        <p className="text-sm text-secondary-900">
          Charity fundraising is a <strong className="font-semibold">concept for the walkthrough</strong>. The figures below are sample data to show how tournament proceeds could be tracked and reported across regions — no real payments or donations are processed.
        </p>
      </div>

      <div className="grid grid-cols-2 xl:grid-cols-4 gap-4">
        <Kpi label="Total raised (sample)" value={formatUSD(CHARITY_TOTAL_RAISED)} note="All regions" icon="ri-hand-coin-line" />
        <Kpi label="Active programs" value={String(CHARITY_PROGRAMS.length)} note="Charity tournaments" icon="ri-trophy-line" />
        <Kpi label="Avg per program" value={formatUSD(Math.round(CHARITY_TOTAL_RAISED / CHARITY_PROGRAMS.length))} note="Sample average" icon="ri-line-chart-line" />
        <Kpi label="Matching partners" value="6" note="Concept add-on" icon="ri-shield-star-line" />
      </div>

      {/* Concepts */}
      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4">
        {CHARITY_CONCEPTS.map((c) => (
          <div key={c.title} className="bg-background-50 border border-background-200 rounded-lg p-4">
            <span className="w-10 h-10 rounded-md bg-secondary-100 text-secondary-700 flex items-center justify-center text-xl"><i className={c.icon}></i></span>
            <p className="mt-3 font-heading font-bold text-base text-foreground-950 leading-snug">{c.title}</p>
            <p className="mt-1.5 text-xs text-foreground-600 leading-relaxed">{c.text}</p>
          </div>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-5">
        {/* Programs */}
        <div className="lg:col-span-7 bg-background-50 border border-background-200 rounded-lg overflow-hidden">
          <div className="px-4 md:px-5 py-3.5 border-b border-background-200 flex flex-wrap items-center justify-between gap-2">
            <p className="font-heading font-bold text-lg text-foreground-950">Fundraising by event</p>
            <div className="inline-flex flex-wrap gap-1 bg-background-100 border border-background-200 rounded-full p-1">
              {scopeOptions.map((o) => (
                <button key={o.id} type="button" onClick={() => setScope(o.id)} className={`px-3 py-1.5 rounded-full font-label font-semibold uppercase tracking-wide text-[10px] whitespace-nowrap cursor-pointer transition-colors ${scope === o.id ? 'bg-foreground-950 text-background-50' : 'text-foreground-600 hover:text-foreground-950'}`}>{o.label}</button>
              ))}
            </div>
          </div>
          <ul className="divide-y divide-background-200">
            {programs.map((p) => {
              const pct = Math.min(100, Math.round((p.raised / p.goal) * 100));
              return (
                <li key={p.id} className="px-4 md:px-5 py-4">
                  <div className="flex items-center justify-between gap-3">
                    <div className="min-w-0">
                      <p className="text-sm font-medium text-foreground-950 truncate">{p.name}</p>
                      <p className="text-xs text-foreground-500 truncate">{regionName(p.regionId)} · {p.beneficiaries}</p>
                    </div>
                    <p className="font-label font-semibold text-foreground-700 whitespace-nowrap">{formatUSD(p.raised)} <span className="text-foreground-400">/ {formatUSD(p.goal)}</span></p>
                  </div>
                  <div className="mt-2 h-2 rounded-full bg-background-200 overflow-hidden">
                    <div className="h-full bg-primary-500 rounded-full" style={{ width: `${pct}%` }}></div>
                  </div>
                </li>
              );
            })}
            {programs.length === 0 && <li className="px-5 py-8 text-center text-sm text-foreground-500">No programs in this scope.</li>}
          </ul>
        </div>

        {/* By region */}
        <div className="lg:col-span-5 space-y-5">
          <div className="bg-background-50 border border-background-200 rounded-lg p-4 md:p-5">
            <p className="font-heading font-bold text-lg text-foreground-950">Raised by region</p>
            <ul className="mt-4 space-y-3.5">
              {REGIONS.map((r) => {
                const max = Math.max(...Object.values(CHARITY_BY_REGION), 1);
                return (
                  <li key={r.id}>
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-foreground-800">{r.city}</span>
                      <span className="font-label font-semibold text-foreground-600">{formatUSD(CHARITY_BY_REGION[r.id])}</span>
                    </div>
                    <div className="mt-1.5 h-2 rounded-full bg-background-200 overflow-hidden">
                      <div className="h-full bg-accent-500 rounded-full" style={{ width: `${(CHARITY_BY_REGION[r.id] / max) * 100}%` }}></div>
                    </div>
                  </li>
                );
              })}
            </ul>
          </div>

          <div className="bg-background-100 border border-background-200 rounded-lg p-4 md:p-5">
            <p className="font-label uppercase tracking-widest text-[10px] font-bold text-foreground-700">Selected scope</p>
            <p className="mt-1.5 font-heading font-extrabold text-2xl text-foreground-950">{formatUSD(raisedForScope)}</p>
            <p className="text-xs text-foreground-500">raised of {formatUSD(goalForScope)} goal · {programs.length} event{programs.length === 1 ? '' : 's'}</p>
          </div>
        </div>
      </div>
    </div>
  );
}